import { useEffect, useState } from 'react';
import { CpmButton, CpmModal } from '../../../../components/design-system';
import {
    CPM_EXCISE_CATEGORY_MAX_LENGTH,
    CPM_EXCISE_SUBCATEGORY_MAX_LENGTH,
} from '../../../../constants/centralProductMaster.constants';

interface AddCategoryModalProps {
    open: boolean;
    stateName: string;
    onClose: () => void;
    onSave: (category: string, subNames: string[]) => Promise<void>;
    saving?: boolean;
}

type Phase = 'form' | 'error' | 'done';

export default function AddCategoryModal({
    open,
    stateName,
    onClose,
    onSave,
    saving = false,
}: AddCategoryModalProps) {
    const [newName, setNewName] = useState('');
    const [newSubs, setNewSubs] = useState('');
    const [phase, setPhase] = useState<Phase>('form');
    const [localSaving, setLocalSaving] = useState(false);
    const [savedName, setSavedName] = useState('');

    useEffect(() => {
        if (open) {
            setNewName('');
            setNewSubs('');
            setPhase('form');
            setLocalSaving(false);
            setSavedName('');
        }
    }, [open]);

    const busy = saving || localSaving;
    const canSave = newName.trim().length > 0 && !busy;
    const placeholder =
        stateName === 'Maharashtra'
            ? 'e.g. FLR 3/A'
            : stateName === 'Assam'
              ? 'e.g. Brand Tier 1'
              : 'e.g. IMFL';

    const handleSave = async () => {
        const name = newName.trim();
        if (!name) return;
        const subs = newSubs
            .split('\n')
            .map((x) => x.trim())
            .filter(Boolean)
            .map((x) => x.slice(0, CPM_EXCISE_SUBCATEGORY_MAX_LENGTH));
        setLocalSaving(true);
        try {
            await onSave(name.slice(0, CPM_EXCISE_CATEGORY_MAX_LENGTH), subs);
            setSavedName(name.slice(0, CPM_EXCISE_CATEGORY_MAX_LENGTH));
            setPhase('done');
        } catch {
            setPhase('error');
        } finally {
            setLocalSaving(false);
        }
    };

    const finish = () => {
        setPhase('form');
        onClose();
    };

    return (
        <CpmModal open={open} onClose={finish} maxWidth={560} zIndex={70}>
            {phase === 'form' ? (
                <>
                    <div
                        className="flex items-start border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 14, padding: '22px 24px 18px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-brand-purple)]"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: 'var(--cpm-purple-tint-13)',
                            }}
                        >
                            <svg
                                width="19"
                                height="19"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                            >
                                <path d="M12 5v14" />
                                <path d="M5 12h14" />
                            </svg>
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em' }}
                            >
                                {stateName} · excise category
                            </span>
                            <h2 className="m-0 !text-[21px] !font-extrabold !tracking-[-0.02em] !leading-[1.15] !text-[var(--cpm-brand-navy)]">
                                Add a category
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                Enter it exactly as {stateName}&apos;s excise department writes it.
                                Subcategories are optional.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col" style={{ padding: '20px 24px', gap: 16 }}>
                        <div className="flex flex-col" style={{ gap: 6 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
                            >
                                Category name
                            </span>
                            <input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                maxLength={CPM_EXCISE_CATEGORY_MAX_LENGTH}
                                placeholder={placeholder}
                                className="rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-brand-navy)]"
                                style={{
                                    height: 42,
                                    padding: '0 13px',
                                    fontSize: 14.5,
                                    fontWeight: 800,
                                }}
                                autoFocus
                            />
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                Stored as{' '}
                                <span className="font-mono" style={{ fontWeight: 700 }}>
                                    category
                                </span>{' '}
                                on this state&apos;s excise taxonomy.
                            </span>
                        </div>
                        <div className="flex flex-col" style={{ gap: 6 }}>
                            <div className="flex items-baseline" style={{ gap: 8 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    Subcategories
                                </span>
                                <span
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    Optional — one per line
                                </span>
                            </div>
                            <textarea
                                value={newSubs}
                                onChange={(e) => setNewSubs(e.target.value)}
                                rows={4}
                                placeholder={'Premium\nDeluxe\nRegular'}
                                className="rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-brand-navy)] resize-y"
                                style={{
                                    padding: '11px 13px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: 1.6,
                                }}
                            />
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                Leave blank if this state&apos;s department doesn&apos;t split this
                                category.
                            </span>
                        </div>
                    </div>

                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '16px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <span
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            Created as ACTIVE
                        </span>
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton
                                variant="outline"
                                size="modal"
                                onClick={finish}
                                disabled={busy}
                            >
                                Cancel
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                onClick={handleSave}
                                disabled={!canSave}
                                isLoading={busy}
                            >
                                Save category
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'error' ? (
                <>
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ padding: '34px 30px 22px', gap: 12 }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 52, height: 52, borderRadius: 14 }}
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            Couldn&apos;t save the category
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            The catalog service did not accept the new category. Nothing was written
                            to {stateName}&apos;s taxonomy. Try again.
                        </span>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '14px 22px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={finish}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                onClick={() => void handleSave()}
                                isLoading={busy}
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'done' ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ padding: '40px 30px 30px', gap: 12 }}
                >
                    <span
                        className="inline-flex items-center justify-center bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]"
                        style={{ width: 52, height: 52, borderRadius: 14 }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path
                                d="M20 6 9 17l-5-5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                    <span
                        className="text-[var(--cpm-success-text-light)]"
                        style={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.45 }}
                    >
                        Saved to {stateName}&apos;s taxonomy.
                    </span>
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}
                    >
                        {savedName || 'Category'} added
                    </span>
                    <CpmButton variant="navy" size="md" className="mt-1.5" onClick={finish}>
                        Back to {stateName}
                    </CpmButton>
                </div>
            ) : null}
        </CpmModal>
    );
}
